import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'

/**
 * NOTE: The resulting PDF is RASTERIZED. The page is a bitmap image captured
 * from the DOM, so the text inside the PDF is NOT selectable or searchable —
 * this is the deliberate tradeoff we accept in exchange for pixel-perfect
 * visual fidelity (fonts, gradients, shadows, rounded cards all render exactly
 * as they appear on screen).
 *
 * The report is exported as ONE continuous full-bleed page rather than one
 * page per section. The old per-section approach produced several tiny
 * trailing pages (a short "System Dynamics" / "Assessment" page each). A
 * single tall page keeps the whole report on one clean sheet with no awkward
 * short pages, and — because one section (the driver analysis) is taller than
 * an A4 sheet — avoids the ugly shrink-to-fit an A4 layout would force.
 */

// Background used for the full-bleed capture. Must match the page canvas so the
// bleed around the content blends seamlessly (no stray white margins/edges).
const EXPORT_BACKGROUND = '#ffffff'

/** Wait for every <img> inside `el` to finish loading (or error) before capture. */
const waitForImages = (el: HTMLElement): Promise<void> => {
  const images = Array.from(el.querySelectorAll('img'))
  const pending = images.filter(
    (img) => !img.complete || img.naturalWidth === 0,
  )
  if (pending.length === 0) return Promise.resolve()

  return Promise.all(
    pending.map(
      (img) =>
        new Promise<void>((resolve) => {
          // Resolve on load OR error so a single broken asset can't hang export.
          const done = () => {
            img.removeEventListener('load', done)
            img.removeEventListener('error', done)
            resolve()
          }
          img.addEventListener('load', done)
          img.addEventListener('error', done)
          // Safety timeout in case the events never fire.
          setTimeout(done, 4000)
        }),
    ),
  ).then(() => undefined)
}

const nextFrame = () =>
  new Promise<void>((resolve) => requestAnimationFrame(() => resolve()))

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export async function exportReportToPdf(
  fileName = 'ooh-attention-report.pdf',
): Promise<void> {
  // 1. Signal the app we're exporting. Components that gate content behind
  //    scroll/viewport animations (opacity:0 until in view), carousels, or
  //    tabs listen for this and switch to a static, fully-expanded, full
  //    opacity layout so nothing captures blank or partially.
  window.dispatchEvent(new CustomEvent('report:export-start'))
  document.body.classList.add('is-exporting')

  try {
    // Give React/CSS a couple of frames + a beat to settle the static layout
    // and let any freshly-revealed content paint before we capture.
    await nextFrame()
    await nextFrame()
    await delay(250)

    // Capture the whole report in one shot as a single continuous page.
    const target =
      (document.getElementById('report-root') as HTMLElement | null) ||
      (document.querySelector('main') as HTMLElement | null)

    if (!target) return

    await waitForImages(target)

    const canvas = await html2canvas(target, {
      scale: 2, // retina-sharp
      useCORS: true, // allow cross-origin images (CDN assets) into the canvas
      backgroundColor: EXPORT_BACKGROUND,
      logging: false,
      windowWidth: document.documentElement.offsetWidth,
    })

    const imgData = canvas.toDataURL('image/png')
    const w = canvas.width
    const h = canvas.height

    // Single page sized exactly to the captured image, image placed at 0,0
    // filling the whole page → full-bleed, no margins.
    const pdf = new jsPDF({
      orientation: w >= h ? 'landscape' : 'portrait',
      unit: 'px',
      format: [w, h],
      hotfixes: ['px_scaling'],
    })
    pdf.addImage(imgData, 'PNG', 0, 0, w, h)
    pdf.save(fileName)
  } finally {
    // 2. Always restore the interactive layout, even if capture throws.
    document.body.classList.remove('is-exporting')
    window.dispatchEvent(new CustomEvent('report:export-end'))
  }
}
